import { useState } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';

/**
 * Everything around an invited match that is not the match (SPEC-011 PR-11.4).
 *
 * The lobby, for a feature that deliberately has no lobby. A match is reachable
 * only by somebody holding its link (ADR-0006), so all this has to do is make a
 * link, show it, and explain the four ways opening one can fail.
 *
 * Every failure resolves to a stated message with the local game still one tap
 * away, which is the posture SPEC-009 set for the shared board and it matters
 * more here: the rules for `legends/matches/…` live in another repository and
 * are deployed from there (CON-003), so "the server said no" is a state this
 * screen genuinely reaches — including, on the day this shipped, because those
 * rules had not been deployed yet.
 */
export default function OvertakeInvite({
    invite,
    onSignIn,
    onCreate,
}) {
    const t = useTranslate();

    // --- Not in a match: the offer to start one --------------------------
    if (!invite.matchId) {
        return (
            <section className="overtake-invite">
                {/* An `h3`, not an `h2`: this plate is the second answer to the
                    question `OvertakeSetup` asks above it, and the two answers
                    are peers (D-28). */}
                <h3 className="overtake-invite__title">{t('play.invite.title')}</h3>
                <p className="overtake-invite__intro">{t('play.invite.intro')}</p>
                {invite.needsAccount ? (
                    <button type="button" className="button button--quiet" onClick={onSignIn}>
                        {t('play.invite.signIn')}
                    </button>
                ) : (
                    <button type="button" className="button button--quiet" onClick={onCreate}>
                        {t('play.invite.create')}
                    </button>
                )}
            </section>
        );
    }

    // --- In a match, but not yet playing one ------------------------------
    if (invite.needsAccount) {
        return (
            <InviteNotice message={t('play.invite.signInToJoin')} onLeave={invite.leave}>
                <button type="button" className="button button--primary" onClick={onSignIn}>
                    {t('play.invite.signIn')}
                </button>
            </InviteNotice>
        );
    }

    if (invite.status === 'loading') {
        return <InviteNotice message={t('play.invite.loading')} onLeave={invite.leave} />;
    }

    if (invite.status === 'missing') {
        return <InviteNotice message={t('play.invite.missing')} onLeave={invite.leave} />;
    }

    if (invite.status === 'refused') {
        return <InviteNotice message={t('play.invite.refused')} onLeave={invite.leave} />;
    }

    if (invite.status === 'failed') {
        return <InviteNotice message={t('play.invite.failed')} onLeave={invite.leave} />;
    }

    if (invite.canJoin) {
        return (
            <InviteNotice
                message={t('play.invite.join', { name: invite.document.names.player })}
                onLeave={invite.leave}
            >
                <button type="button" className="button button--primary" onClick={invite.join}>
                    {t('play.invite.take')}
                </button>
            </InviteNotice>
        );
    }

    // Seated by neither name: somebody was sent a link to a match that already
    // has two players. They have done nothing wrong, so this is a sentence
    // rather than an error.
    if (!invite.seat) {
        return <InviteNotice message={t('play.invite.taken')} onLeave={invite.leave} />;
    }

    // The host, waiting. This is the screen the link is copied from.
    if (!invite.document.seats.opponent) {
        return (
            <InviteNotice message={t('play.invite.waiting')} onLeave={invite.leave}>
                <ShareLink />
            </InviteNotice>
        );
    }

    return null;
}

/**
 * The link, as something to copy.
 *
 * A readonly input beside a button rather than a bare "copy" control, and the
 * input is what makes it work everywhere: `navigator.clipboard` needs a secure
 * context and a permission that Safari can refuse, and a copy button that
 * silently does nothing is worse than no copy button. The link is visible and
 * selectable either way, so the fallback is the thing itself.
 */
function ShareLink() {
    const t = useTranslate();
    const [copied, setCopied] = useState(false);
    const url = typeof window === 'undefined' ? '' : window.location.href;

    return (
        <div className="overtake-invite__share">
            <input
                className="overtake-invite__link"
                type="text"
                readOnly
                value={url}
                aria-label={t('play.invite.link')}
                onFocus={(event) => event.target.select()}
            />
            <button
                type="button"
                className="button button--quiet"
                onClick={() => {
                    navigator.clipboard?.writeText(url).then(
                        () => setCopied(true),
                        () => setCopied(false),
                    );
                }}
            >
                {t(copied ? 'play.invite.copied' : 'play.invite.copy')}
            </button>
        </div>
    );
}

/** One sentence, whatever else is going on, and the way back to a local game. */
function InviteNotice({ message, onLeave, children }) {
    const t = useTranslate();

    return (
        <section className="overtake-invite" aria-live="polite">
            <p className="overtake-invite__notice">{message}</p>
            {children}
            <button type="button" className="button button--quiet" onClick={onLeave}>
                {t('play.invite.back')}
            </button>
        </section>
    );
}
